import classNames from 'classnames';
import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DEPTH, TRADE_TYPE, TRADE_TYPE_LANG_MAP } from 'src/common/constants';
import { contractNameFormatter, currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppSelector } from 'src/hooks';
import { FutureSymbol, MyTrade, OrderTableContentProps } from 'src/types';

const ClosePLTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterClosePLOrders, setFilterClosePLOrders] = useState([] as Array<MyTrade>);
    const currentSymbol: FutureSymbol = useAppSelector((state) => state.derivatives.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const { myTrades } = useAppSelector((state) => state.derivatives);
    const futureSymbols = useAppSelector((state) => state.global.configV2.futuresSymbol);
    useEffect(() => {
        setFilterClosePLOrders(
            myTrades
                .filter(
                    (myTrade) =>
                        (isShowAll || myTrade.symbolId === symbolId) &&
                        (myTrade.side === TRADE_TYPE.BUY_CLOSE ||
                            myTrade.side === TRADE_TYPE.SELL_CLOSE),
                )
                .sort((a, b) => (a.time - b.time > 0 ? -1 : 1)),
        );
    }, [myTrades, symbolId, isShowAll]);

    const columns = [
        {
            title: (
                <FlexContainer styles={{ alignItems: 'center' }}>{t('contracts')}</FlexContainer>
            ),
            key: 'contracts',
            width: '14%',
            render: (row: { [x: string]: any }) => {
                return (
                    <div
                        className={classNames(
                            'contract',
                            {
                                borderGreen: row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                borderRed: row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                            {
                                greenText: row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText: row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {contractNameFormatter(row['symbolName'])}
                    </div>
                );
            },
        },
        {
            title: t('qty'),
            key: 'qty',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            'centerText',
                            {
                                greenText: row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText: row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {row['quantity']}
                    </span>
                );
            },
        },
        {
            title: t('exit_price'),
            key: 'exitPrice',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['price'],
                            DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                            DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('trade_type'),
            key: 'tradeType',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText: row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText: row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {t(TRADE_TYPE_LANG_MAP[row['side']])}
                    </span>
                );
            },
        },
        {
            title: t('closed_pl'),
            key: 'closedPL',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {currencyFormatter(
                            row['pnl'],
                            DEPTH[
                                Number(
                                    futureSymbols[row.symbolId]?.baseTokenFutures?.marginPrecision,
                                )
                            ],
                            DEPTH[
                                Number(
                                    futureSymbols[row.symbolId]?.baseTokenFutures?.marginPrecision,
                                )
                            ],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('trade_time'),
            key: 'tradeTime',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return <span>{DateTimeFormatter(Number(row['time']))}</span>;
            },
        },
    ];

    return <StyledTable columns={columns} data={filterClosePLOrders} rowKey="tradeId" />;
};

export { ClosePLTable };
